import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';

/**
 * Fullscreen image viewer component displayed via CDK Overlay.
 * Supports zoom in/out via buttons and mouse wheel.
 * Closes on Escape key press or close button click.
 */
@Component({
  selector: 'rms-image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss'],
  standalone: false
})
export class ImageViewerComponent {

  @Input() imageUrl = '';
  @Output() closeViewer = new EventEmitter<void>();

  /** Current zoom level as a percentage (100 = original fit) */
  zoomLevel = 100;

  /** Base width in pixels captured when image first loads at 100% zoom */
  baseWidth = 0;

  private readonly ZOOM_STEP = 25;
  readonly MIN_ZOOM = 25;
  readonly MAX_ZOOM = 400;

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeViewer.emit();
  }

  /** Captures the image's rendered width at 100% zoom to use as the zoom base */
  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    this.baseWidth = img.clientWidth;
  }

  /** Returns pixel width for the current zoom level, or null at default zoom */
  get zoomedWidth(): number | null {
    if (this.zoomLevel === 100 || this.baseWidth === 0) {
      return null;
    }
    return this.baseWidth * this.zoomLevel / 100;
  }

  /** Zoom in by one step */
  zoomIn(): void {
    if (this.zoomLevel < this.MAX_ZOOM) {
      this.zoomLevel = Math.min(this.zoomLevel + this.ZOOM_STEP, this.MAX_ZOOM);
    }
  }

  /** Zoom out by one step */
  zoomOut(): void {
    if (this.zoomLevel > this.MIN_ZOOM) {
      this.zoomLevel = Math.max(this.zoomLevel - this.ZOOM_STEP, this.MIN_ZOOM);
    }
  }

  /** Reset zoom to fit viewport */
  resetZoom(): void {
    this.zoomLevel = 100;
  }

  /** Handle mouse wheel zoom on the image container */
  onWheel(event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  /** Close the viewer when clicking the background area (not the image or controls) */
  onBackgroundClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('image-viewer-container') ||
        target.classList.contains('image-viewer-scroll-area')) {
      this.closeViewer.emit();
    }
  }
}
